import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { NAV_ICONS, NavIcon } from "./navIcons";
// The Settings rows' own glyphs, from the same set the rail is drawn from and
// already inked in #00424B, so a Branding row and a Settings row are the same
// mark in the same hand.
import { ReactComponent as BillingsIcon } from "../../assets/svg/sidebar/Billings.svg";
import { ReactComponent as SupportIcon } from "../../assets/svg/sidebar/Support.svg";
import { ReactComponent as AccountsIcon } from "../../assets/svg/sidebar/Accounts.svg";
import { ReactComponent as PolicyIcon } from "../../assets/svg/sidebar/Policy.svg";
import { ReactComponent as LogoutIcon } from "../../assets/svg/sidebar/Logout.svg";
import { LogoutConfirmModal } from "../../features/auth/components/LogoutConfirmModal";
import { useNavigationGuard } from "./UnsavedChanges";
import { transitionState } from "./PageTransition";
import { useAccountSummary } from "./accountSummary";
import { ROUTES, settingsTabPath } from "../../routes/paths";
import TrialMeter from "../../features/billing/components/TrialMeter";
import TrialBanner from "../../features/billing/components/TrialBanner";

interface MenuItem {
  /**
   * Where the row goes — a route, or a Settings tab's `?tab=`. Absent on a row
   * that acts rather than navigates; see `action`.
   */
  to?: string;
  label: string;
  Icon: NavIcon;
  /**
   * What the row does instead of going somewhere. Logout is the one such row:
   * it asks before it acts, so it opens the dialog rather than a screen.
   */
  action?: "logout";
}

interface MenuSection {
  /**
   * What the group is called, where it is a group. Absent on the one that is a
   * single row: a heading reading "Revenue" over a row reading "Revenue" is
   * the word twice, and the row is already its own name.
   */
  title?: string;
  items: MenuItem[];
}

/**
 * Everything the tab bar has no room for, in the groups the design puts them
 * in — Revenue on its own at the head, then Branding, then Settings.
 *
 * Branding is the rail's own three destinations, drawn with the rail's own
 * glyphs — one mapping in `NAV_ICONS`, so a screen is known by the same mark
 * whichever navigation reached it.
 *
 * Settings is four tabs of one screen rather than four screens, so there is no
 * rail item to take a glyph from; they are imported from the same set by hand
 * instead. General is missing on purpose — it is the profile, and the name at
 * the top of this menu is what opens it. Logout closes the group: it is not a
 * tab, but it is where somebody looks for it, and the phone has nowhere else
 * to put it — the rail's account block, which offers it on a desktop, is the
 * thing this menu replaces.
 *
 * Create plan is missing too, for the reason the rail gives: it is the one
 * screen a phone cannot carry, so it is left off rather than offered and
 * disappointing.
 */
const SECTIONS: MenuSection[] = [
  {
    // Revenue, alone and first. It held the circle on the tab bar until My
    // task had a phone layout to show, and this is where it went rather than
    // into the Branding group under it: what the practice earned is not part
    // of how the practice presents itself, and filing it there would have hidden
    // it among three screens about a brand.
    //
    // First, because of the two readings this menu holds it is the one anybody
    // comes here on purpose for. Settings is where a thing is changed and
    // Branding is set up once; Revenue is a question asked again every week,
    // and it sits directly under the account whose money it is counting.
    items: [
      {
        to: ROUTES.revenue,
        label: "Revenue",
        Icon: NAV_ICONS[ROUTES.revenue],
      },
    ],
  },
  {
    title: "Branding",
    items: [
      {
        to: ROUTES.myBrand,
        label: "My brand",
        Icon: NAV_ICONS[ROUTES.myBrand],
        // One row, and no tabs under it. The phone's My brand is a single
        // screen now — what the brand sets up, and where it is set up — so a
        // list of six tabs here would be six ways to the same picture. See
        // `MyBrandMobilePage`.
      },
      {
        to: ROUTES.createPage,
        label: "Create page",
        Icon: NAV_ICONS[ROUTES.createPage],
      },
      {
        to: ROUTES.launchApp,
        label: "Launch app",
        Icon: NAV_ICONS[ROUTES.launchApp],
      },
    ],
  },
  {
    title: "Settings",
    items: [
      { to: settingsTabPath("billings"), label: "Billings", Icon: BillingsIcon },
      { to: settingsTabPath("support"), label: "Support", Icon: SupportIcon },
      { to: settingsTabPath("accounts"), label: "Accounts", Icon: AccountsIcon },
      { to: settingsTabPath("policy"), label: "Policy", Icon: PolicyIcon },
      { action: "logout", label: "Logout", Icon: LogoutIcon },
    ],
  },
];

export interface MobileAccountMenuProps {
  onClose: () => void;
}

/**
 * The phone's other half of the navigation: the account, and the destinations
 * the tab bar has no room for.
 *
 * A screen rather than the rail in a drawer. The rail is a column of nine
 * equal items built for a viewport that can hold them all at once; on a phone
 * that same list arrives as a wall of choices with the one thing somebody
 * pressed their own face to reach — their account — squeezed into the last
 * 60px of it. So the phone gets what a phone expects instead: who is signed in
 * at the top, what is left of the trial under it, and the rest grouped under
 * the two words that say what they are.
 *
 * The rows are buttons rather than links, unlike the tab bar's. A link would be
 * the better answer — the status bar would say where it leads — but every one
 * of these is a press away from a half-filled form, so each goes through the
 * guard first. See `UnsavedChanges`.
 *
 * Mounted only while it is open, and the shell above decides that. Everything
 * on it is either read from a cached query or already in memory, so there is
 * nothing to keep warm behind the page — and a menu left in the tree would put
 * a second trial meter and a second account in it, which is one account too
 * many for anything reading the page.
 */
const MobileAccountMenu: React.FC<MobileAccountMenuProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const guardNavigation = useNavigationGuard();
  const {
    displayName,
    imageUrl,
    accountLine,
    isSubscribed,
    trial,
    lapse,
  } = useAccountSummary();

  // Whether the "Log out?" dialog is up. The menu stays mounted behind it —
  // cancelling should put the reader back where they were, and a signed-out
  // session navigates to the login screen, which drops the state this menu is
  // opened by and so takes the menu with it. See `AppLayout`.
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  // Opening, not swapping: a row here is pressed on a screen that came in
  // over the app, and the screen it leads to should come in the same way. The
  // paths alone would call it a swap — every destination is a section's own
  // screen, the same as the tab bar's — so the navigation says which it is.
  // See `pageDirection`.
  const go = (to: string) =>
    guardNavigation(() => navigate(to, { state: transitionState("forward") }));

  return (
    <nav
      // A landmark rather than a plain box: it is half of this app's
      // navigation on a phone — the tab bar across the foot is the other — and
      // a screen reader should be able to reach it as one. Named for what is
      // on it, so the two are told apart by more than their order.
      aria-label="Account and settings"
      // Over the whole shell, tab bar included: this is a screen rather than a
      // panel on one, and it comes in from the right the way a phone moves
      // forward a page — which is what makes the chevron at its head a way
      // back. `md:hidden` is belt and braces; from `md` up nothing renders it.
      className="md:hidden fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white animate-screen-in"
    >
      {/* The head clears the status bar and the camera the same way the shell's
          top bar does — the inset plus the padding the row would have had.

          The way back and who is signed in share that row. They were stacked,
          which spent a chevron's worth of height on an otherwise empty line
          and pushed the list down the screen; side by side they cost one row
          between them, and everything under them starts that much higher. */}
      <div className="flex-shrink-0 flex items-center gap-1 px-4 pb-2 pt-[calc(env(safe-area-inset-top)_+_1rem)]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close account menu"
          className="-ml-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[#00424B] transition-colors active:bg-[#EFFBFD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        {/* Who is signed in, and the way into their own profile. The rail's
            block leads nowhere — it sits under a Settings item that is always
            on screen. Here it is the only way to General, so the whole row is
            the target rather than a link inside it.

            The avatar is the chevron's own height rather than the 56px it
            stood at alone: beside a back button it has to sit inside that
            button's row, and a face taller than it would cost back the line
            the two of them just saved. */}
        <button
          type="button"
          onClick={() => go(settingsTabPath("general"))}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-[8px] px-2 py-1.5 text-left transition-colors active:bg-[#F8FEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EDE7F6]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-[17px] font-semibold text-[#5B3FA8]">
                {displayName.charAt(0).toUpperCase()}
              </span>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[17px] font-semibold leading-tight text-[#00424B]">
              {displayName}
            </span>
            {/* The tier, or the address — the same line the rail draws, in the
                same two colours. A non-breaking space where there is nothing
                to print, so the row keeps its height either way. */}
            <span
              className={`block truncate text-[13px] leading-tight ${
                isSubscribed ? "text-[#00BACB] font-medium" : "text-[#8B8D98]"
              }`}
            >
              {accountLine || " "}
            </span>
          </span>
        </button>
      </div>

      <div className="flex-1 px-4 pb-[calc(env(safe-area-inset-bottom)_+_1.5rem)]">
        {/* How much of the free trial is left, or the way onto a plan at the
            end of it — the same pair the foot of the rail carries, and exactly
            one of them is ever drawn. See `TrialMeter`. */}
        <div className="mt-3">
          <TrialMeter trial={trial} />
          <TrialBanner trial={trial} ended={lapse} />
        </div>

        {SECTIONS.map((section, index) => (
          <section
            key={section.title ?? "top"}
            // A group with no heading is still a landmark, and still has to say
            // what is in it — the heading is what names the others.
            aria-label={section.title ?? "Your practice"}
          >
            {/* One rule, over the first group: it is what separates the
                account from the navigation under it, and it is drawn here
                rather than under the trial card because a workspace on a plan
                has no card for it to hang off. The groups after it need none —
                a heading with a gap over it is already a break, and a second
                rule would read as the end of something.

                `mt-6` and no top padding on the later ones, so both sit the
                same distance below the row above them.

                The first group has no heading now, so the rule falls through
                onto its list instead — same line, same distance, hung off
                whichever of the two the group actually begins with. */}
            {section.title && (
              <h2
                className={`mt-6 text-[16px] font-semibold text-ink ${
                  index === 0 ? "border-t border-[#EDEFF3] pt-6" : ""
                }`}
              >
                {section.title}
              </h2>
            )}
            <ul
              className={
                index === 0 && !section.title
                  ? "mt-6 border-t border-[#EDEFF3] pt-1"
                  : "mt-1"
              }
            >
              {section.items.map((item) => (
                // The hairline goes between the rows and not under the last of
                // them: the next heading is what ends a group.
                <li
                  key={item.label}
                  className="border-b border-[#EDEFF3] last:border-b-0"
                >
                  <button
                    type="button"
                    // Every row goes where it says, bar the one that asks
                    // first: Logout opens the dialog instead.
                    onClick={() => {
                      if (item.action === "logout") {
                        setConfirmingLogout(true);
                      } else if (item.to) {
                        go(item.to);
                      }
                    }}
                    className="flex w-full items-center gap-4 py-4 text-left transition-colors active:bg-[#F8FEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
                  >
                    {/* The rail's glyphs already draw in its ink; the lucide
                        ones are told to, so a Branding row and a Settings row
                        are the same weight of mark. */}
                    <span className="h-[22px] w-[22px] flex-shrink-0 text-[#00424B]">
                      <item.Icon className="h-full w-full" />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[16px] text-[#00424B]">
                      {item.label}
                    </span>
                    {/* Every row goes somewhere, so every row points the way.
                        My brand opened six tabs under it until the phone's own
                        version of that screen replaced them — see
                        `MyBrandMobilePage`. */}
                    <ChevronRight
                      className="h-5 w-5 flex-shrink-0 text-[#00424B]"
                      aria-hidden="true"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/* The same question the rail and the Accounts tab ask, asked the same
          way — a sheet up from the bottom edge on a phone. See
          `LogoutConfirmModal`. */}
      <LogoutConfirmModal
        open={confirmingLogout}
        onClose={() => setConfirmingLogout(false)}
      />
    </nav>
  );
};

export default MobileAccountMenu;
